import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { MOCK_DESIGNS } from '../../lib/mockData';

// ─── OccasionPicker ───────────────────────────────────────────────────────────
// Parents with children = non-selectable header + expand/collapse toggle.
// Parents without children = selectable pill (leaf node, legacy flat mode).
// Subcategories only show when their parent is expanded.
function OccasionPicker({ categories, selected, onChange }) {
  const parents  = categories.filter(c => !c.parent_id);
  const children = categories.filter(c =>  c.parent_id);
  const [expandedParents, setExpandedParents] = useState(() => {
    // Auto-expand any parent that has a selected child
    return new Set(
      children.filter(ch => selected.includes(ch.id)).map(ch => ch.parent_id)
    );
  });

  // Flat legacy fallback — no parent structure yet
  if (parents.length === 0) {
    return (
      <div className="flex flex-wrap gap-2">
        {categories.map(cat => (
          <button key={cat.id} type="button" onClick={() => onChange(cat.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
              selected.includes(cat.id)
                ? 'bg-blush-300 border-blush-300 text-white'
                : 'bg-white border-sage-100 text-sage-500 hover:border-blush-200'
            }`}>
            {cat.label}
          </button>
        ))}
      </div>
    );
  }

  function toggleParent(id) {
    setExpandedParents(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }

  return (
    <div className="border border-sage-100 rounded-2xl overflow-hidden bg-white">
      {parents.map((parent, idx) => {
        const kids       = children.filter(ch => ch.parent_id === parent.id);
        const hasKids    = kids.length > 0;
        const isExpanded = expandedParents.has(parent.id);
        const anyChildSelected = kids.some(k => selected.includes(k.id));
        const isLeafSelected   = !hasKids && selected.includes(parent.id);

        return (
          <div key={parent.id} className={idx > 0 ? 'border-t border-sage-100' : ''}>
            {/* Parent row */}
            <div className="flex items-center gap-3 px-4 py-2.5">
              {hasKids ? (
                // Has children — non-selectable header, click expands children
                <button
                  type="button"
                  onClick={() => toggleParent(parent.id)}
                  className="flex-1 flex items-center justify-between gap-2 text-left group"
                >
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-bold uppercase tracking-widest transition-colors ${
                      anyChildSelected ? 'text-blush-400' : 'text-sage-500 group-hover:text-charcoal'
                    }`}>
                      {parent.label}
                    </span>
                    {anyChildSelected && (
                      <span className="text-[10px] font-bold text-blush-300 bg-blush-50 px-2 py-0.5 rounded-full border border-blush-100">
                        {kids.filter(k => selected.includes(k.id)).length} selected
                      </span>
                    )}
                  </div>
                  <SafeIcon
                    name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                    className="text-xs text-sage-300 group-hover:text-sage-500 shrink-0"
                  />
                </button>
              ) : (
                // Leaf parent (no children) — fully selectable pill
                <button
                  type="button"
                  onClick={() => onChange(parent.id)}
                  className={`flex-1 flex items-center justify-between gap-2 text-left rounded-xl px-1 py-0.5 transition-all group ${
                    isLeafSelected ? 'text-blush-500' : 'text-sage-500 hover:text-charcoal'
                  }`}
                >
                  <span className="text-xs font-bold uppercase tracking-widest">
                    {parent.label}
                  </span>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                    isLeafSelected
                      ? 'bg-blush-300 border-blush-300'
                      : 'border-sage-200 group-hover:border-blush-200'
                  }`}>
                    {isLeafSelected && <SafeIcon name="Check" className="text-white" style={{ fontSize: 10 }} />}
                  </div>
                </button>
              )}
            </div>

            {/* Children — slide in when parent is expanded */}
            <AnimatePresence initial={false}>
              {hasKids && isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  className="overflow-hidden"
                >
                  <div className="flex flex-wrap gap-2 px-5 pb-3 pt-1 bg-sage-50/50 border-t border-sage-100">
                    {kids.map(child => (
                      <button
                        key={child.id}
                        type="button"
                        onClick={() => onChange(child.id)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                          selected.includes(child.id)
                            ? 'bg-blush-300 border-blush-300 text-white'
                            : 'bg-white border-sage-100 text-sage-500 hover:border-blush-200 hover:text-blush-400'
                        }`}
                      >
                        {child.label}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}

export default function DesignLibrary() {
  const [designs, setDesigns] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDesign, setEditingDesign] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', category: [], active: true, main_image_index: 0 });
  const [imageFiles, setImageFiles] = useState({ 1: null, 2: null, 3: null });
  const [previews, setPreviews] = useState({ 1: '', 2: '', 3: '' });
  const [saving, setSaving] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  const fileRef1 = useRef();
  const fileRef2 = useRef();
  const fileRef3 = useRef();
  const formTopRef = useRef();

  useEffect(() => {
    fetchDesigns();
    fetchCategories();
  }, []);

  async function fetchCategories() {
    if (isSupabaseConfigured()) {
      const { data } = await supabase.from('design_categories_1739812345679').select('*').order('sort_order');
      setCategories(data || []);
    }
  }

  async function fetchDesigns() {
    setLoading(true);
    if (isSupabaseConfigured()) {
      const { data } = await supabase.from('designs').select('*').order('sort_order');
      setDesigns(data || []);
    } else {
      setDesigns(MOCK_DESIGNS);
    }
    setLoading(false);
  }

  async function handleAIGenerate() {
    // We need a real file to upload or an existing public URL
    if (!imageFiles[1] && !previews[1]) {
      setError('Please upload an image first so the AI can analyze the design.');
      return;
    }

    setGeneratingAI(true);
    setError('');
    let tempFileName = null;

    try {
      let publicUrl = previews[1];

      // 1. If there's a new file, upload as temp first
      if (imageFiles[1]) {
        tempFileName = `temp/${Date.now()}-preview.jpg`;
        const { error: uploadError } = await supabase.storage
          .from('design-images')
          .upload(tempFileName, imageFiles[1]);
        
        if (uploadError) throw uploadError;
        
        const { data: urlData } = supabase.storage
          .from('design-images')
          .getPublicUrl(tempFileName);
        
        publicUrl = urlData.publicUrl;
      }

      // 2. Fetch all existing design titles to avoid duplicates
      const { data: existingDesigns } = await supabase
        .from('designs')
        .select('name');
      
      const namesArray = (existingDesigns || []).map(d => d.name);

      // 3. Call Edge Function with the specific body requirements
      const { data, error: aiError } = await supabase.functions.invoke('generate-design-content', {
        body: { 
          imageUrl: publicUrl,
          existingTitles: namesArray
        }
      });

      if (aiError) throw aiError;

      // 4. Map returned { title, description } to form
      if (data) {
        setForm(f => ({
          ...f,
          name: data.title || f.name,
          description: data.description || f.description
        }));
      }

    } catch (err) {
      console.error('AI Generation failed:', err);
      setError('AI generation failed. Check if "generate-design-content" is deployed.');
    } finally {
      // 5. Cleanup temp file
      if (tempFileName) {
        await supabase.storage.from('design-images').remove([tempFileName]);
      }
      setGeneratingAI(false);
    }
  }

  function openNew() {
    setError('');
    setEditingDesign(null);
    setForm({ name: '', description: '', category: [], active: true, main_image_index: 0, next_day_available: false });
    setImageFiles({ 1: null, 2: null, 3: null });
    setPreviews({ 1: '', 2: '', 3: '' });
    setShowForm(true);
    setTimeout(() => formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  function openEdit(design) {
    setError('');
    setEditingDesign(design);
    setForm({ 
      name: design.name, 
      description: design.description || '',
      category: design.category || [], 
      active: design.active,
      main_image_index: design.main_image_index || 0,
      next_day_available: design.next_day_available || false
    });
    setPreviews({ 1: design.image_url || '', 2: design.image_url_2 || '', 3: design.image_url_grid || '' });
    setImageFiles({ 1: null, 2: null, 3: null });
    setShowForm(true);
    setTimeout(() => formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  function handleFileChange(e, slot) {
    const file = e.target.files[0];
    if (!file) return;
    setImageFiles(prev => ({ ...prev, [slot]: file }));
    setPreviews(prev => ({ ...prev, [slot]: URL.createObjectURL(file) }));
  }

  function sanitizeFileName(name) {
    // Remove or replace characters invalid in Supabase Storage keys:
    // keep alphanumerics, dots, dashes, underscores — replace everything else with '_'
    return name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
  }

  async function handleSave() {
    if (!previews[1] && !imageFiles[1]) {
      setError('Please upload at least the primary image.');
      return;
    }

    setSaving(true);
    try {
      if (isSupabaseConfigured()) {
        let url1 = previews[1];
        let url2 = previews[2];

        if (imageFiles[1]) {
          const fileName = `designs/${Date.now()}-1-${sanitizeFileName(imageFiles[1].name)}`;
          const { error: e1 } = await supabase.storage.from('design-images').upload(fileName, imageFiles[1]);
          if (e1) throw new Error(`Mockup image upload failed: ${e1.message}`);
          url1 = supabase.storage.from('design-images').getPublicUrl(fileName).data.publicUrl;
        }

        if (imageFiles[2]) {
          const fileName = `designs/${Date.now()}-2-${sanitizeFileName(imageFiles[2].name)}`;
          const { error: e2 } = await supabase.storage.from('design-images').upload(fileName, imageFiles[2]);
          if (e2) throw new Error(`Detail image upload failed: ${e2.message}`);
          url2 = supabase.storage.from('design-images').getPublicUrl(fileName).data.publicUrl;
        }

        let url3 = previews[3];
        if (imageFiles[3]) {
          const fileName = `designs/${Date.now()}-3-${sanitizeFileName(imageFiles[3].name)}`;
          const { error: e3 } = await supabase.storage.from('design-images').upload(fileName, imageFiles[3]);
          if (e3) throw new Error(`Grid thumbnail upload failed: ${e3.message}`);
          url3 = supabase.storage.from('design-images').getPublicUrl(fileName).data.publicUrl;
        }

        const payload = {
          name: form.name,
          description: form.description,
          category: form.category,
          active: form.active,
          main_image_index: form.main_image_index,
          next_day_available: form.next_day_available,
          image_url: url1,
          image_url_2: url2,
          image_url_grid: url3 || null
        };

        if (editingDesign) {
          await supabase.from('designs').update(payload).eq('id', editingDesign.id);
        } else {
          await supabase.from('designs').insert({ ...payload, sort_order: designs.length });
        }
        await fetchDesigns();
      }
      setShowForm(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function toggleSelectMode() {
    setSelectMode(s => !s);
    setSelectedIds(new Set());
    setConfirmBulkDelete(false);
  }

  function toggleSelectDesign(id) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function selectAllVisible(visibleDesigns) {
    if (selectedIds.size === visibleDesigns.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(visibleDesigns.map(d => d.id)));
    }
  }

  async function handleBulkDelete() {
    if (!confirmBulkDelete) {
      setConfirmBulkDelete(true);
      return;
    }
    setBulkDeleting(true);
    try {
      if (isSupabaseConfigured()) {
        const ids = Array.from(selectedIds);
        await supabase.from('designs').delete().in('id', ids);
        await fetchDesigns();
      }
      setSelectedIds(new Set());
      setSelectMode(false);
      setConfirmBulkDelete(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setBulkDeleting(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-serif text-charcoal">Design Library</h2>
          <p className="text-sage-500 text-sm">Manage vase wraps with AI-assisted content.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={toggleSelectMode} className={`px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-colors shadow-soft border ${
            selectMode
              ? 'bg-sage-100 border-sage-200 text-sage-700 hover:bg-sage-200'
              : 'bg-white border-sage-200 text-sage-500 hover:border-sage-300'
          }`}>
            <SafeIcon name={selectMode ? 'X' : 'CheckSquare'} />
            {selectMode ? 'Cancel' : 'Select'}
          </button>
          <button onClick={openNew} className="bg-blush-300 hover:bg-blush-400 text-white px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-colors shadow-soft">
            <SafeIcon name="Plus" /> New Design
          </button>
        </div>
      </div>

      <AnimatePresence>
        <div ref={formTopRef} />
        {showForm && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="bg-white border border-sage-100 rounded-2xl p-6 shadow-soft space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="flex justify-between items-end">
                  <div className="field-group flex-1 mr-4">
                    <label className="form-label">Design Name</label>
                    <input type="text" className="input-field" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Midnight Garden" />
                  </div>
                  <button 
                    onClick={handleAIGenerate}
                    disabled={generatingAI}
                    className="mb-1 p-3 bg-charcoal text-white rounded-xl hover:bg-black transition-all flex items-center gap-2 text-xs font-bold uppercase tracking-widest disabled:opacity-50"
                  >
                    {generatingAI ? <div className="w-4 h-4 border-2 border-white/30 border-t-white animate-spin rounded-full" /> : <SafeIcon name="Cpu" />}
                    AI Generate
                  </button>
                </div>

                <div className="field-group">
                  <label className="form-label">Description (Visible on Zoom)</label>
                  <textarea 
                    className="input-field h-32 resize-none py-4" 
                    value={form.description} 
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Tell the story of this design..."
                  />
                </div>
                
                <div className="field-group">
                  <label className="form-label">
                    Occasions
                    {form.category.length > 0 && (
                      <span className="ml-2 normal-case font-normal text-blush-400">
                        {form.category.length} selected
                      </span>
                    )}
                  </label>
                  <OccasionPicker
                    categories={categories}
                    selected={form.category}
                    onChange={cat => setForm(f => ({
                      ...f,
                      category: f.category.includes(cat)
                        ? f.category.filter(c => c !== cat)
                        : [...f.category, cat]
                    }))}
                  />
                </div>

                <div className="flex flex-col gap-3 pt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} className="w-4 h-4 accent-blush-300" />
                    <span className="text-sm font-medium text-charcoal">Visible to customers</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.next_day_available} onChange={e => setForm(f => ({ ...f, next_day_available: e.target.checked }))} className="w-4 h-4 accent-amber-400" />
                    <span className="text-sm font-medium text-charcoal">⚡ Available for next-day delivery</span>
                  </label>
                </div>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {/* Slot 1 — Overlay/Mockup image */}
                  {[1, 2].map(slot => (
                    <div key={slot} className="space-y-3">
                      <label className="form-label text-center">
                        {slot === 1 ? 'Mockup Image *' : 'Detail Image'}
                      </label>
                      <p className="text-[10px] text-sage-400 text-center -mt-2">
                        {slot === 1 ? 'Used for flower overlay' : 'Shown in zoom modal'}
                      </p>
                      <div
                        onClick={() => (slot === 1 ? fileRef1 : fileRef2).current?.click()}
                        className={`relative aspect-square rounded-2xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-all overflow-hidden ${
                          previews[slot] ? 'border-blush-200' : 'border-sage-100 hover:border-blush-100 bg-sage-50'
                        }`}
                      >
                        {previews[slot] ? (
                          <img src={previews[slot]} className="absolute inset-0 w-full h-full object-cover" alt={`Slot ${slot}`} />
                        ) : (
                          <SafeIcon name="Upload" className="text-2xl text-sage-300" />
                        )}
                      </div>
                      <button
                        onClick={() => setForm(f => ({ ...f, main_image_index: slot - 1 }))}
                        className={`w-full py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all ${
                          form.main_image_index === slot - 1 ? 'bg-blush-400 text-white' : 'bg-sage-50 text-sage-400 hover:bg-sage-100'
                        }`}
                      >
                        {form.main_image_index === slot - 1 ? 'Main Display' : 'Set as Main'}
                      </button>
                      <input ref={slot === 1 ? fileRef1 : fileRef2} type="file" className="hidden" accept="image/*" onChange={e => handleFileChange(e, slot)} />
                    </div>
                  ))}
                </div>

                {/* Slot 3 — Grid thumbnail */}
                <div className="space-y-3 pt-2 border-t border-sage-50">
                  <label className="form-label text-center">Grid Thumbnail</label>
                  <p className="text-[10px] text-sage-400 text-center -mt-2">
                    Tight crop shown in design selection grid — no padding needed
                  </p>
                  <div
                    onClick={() => fileRef3.current?.click()}
                    className={`relative aspect-square rounded-2xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-all overflow-hidden ${
                      previews[3] ? 'border-blush-200' : 'border-sage-100 hover:border-blush-100 bg-sage-50'
                    }`}
                  >
                    {previews[3] ? (
                      <img src={previews[3]} className="absolute inset-0 w-full h-full object-cover" alt="Grid thumbnail" />
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <SafeIcon name="Upload" className="text-2xl text-sage-300" />
                        <p className="text-[10px] text-sage-400 font-medium text-center px-4">Optional — falls back to Mockup Image</p>
                      </div>
                    )}
                  </div>
                  <input ref={fileRef3} type="file" className="hidden" accept="image/*" onChange={e => handleFileChange(e, 3)} />
                </div>
              </div>
            </div>

            {error && <p className="text-red-500 text-xs font-bold text-center">{error}</p>}

            <div className="flex gap-3 pt-4 border-t border-sage-50">
              <button onClick={handleSave} disabled={saving} className="btn-primary flex-1">{saving ? 'Saving...' : 'Save Design'}</button>
              <button onClick={() => setShowForm(false)} className="btn-secondary flex-1">Cancel</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Category filter bar */}
      <div className="space-y-2">
        {/* Main filter row */}
        <div className="flex overflow-x-auto scrollbar-hide gap-2 pb-1">
          <button
            onClick={() => setActiveFilter('all')}
            className={`shrink-0 px-4 py-2 rounded-full text-xs font-bold transition-all ${
              activeFilter === 'all' ? 'bg-charcoal text-white' : 'bg-white border border-sage-200 text-sage-500 hover:border-sage-300'
            }`}
          >
            All ({designs.length})
          </button>
          <button
            onClick={() => setActiveFilter('next_day')}
            className={`shrink-0 px-4 py-2 rounded-full text-xs font-bold transition-all flex items-center gap-1.5 ${
              activeFilter === 'next_day' ? 'bg-amber-400 text-white' : 'bg-white border border-sage-200 text-sage-500 hover:border-amber-200'
            }`}
          >
            ⚡ Next Day ({designs.filter(d => d.next_day_available).length})
          </button>
          {(() => {
            const parents  = categories.filter(c => !c.parent_id);
            const children = categories.filter(c =>  c.parent_id);
            const topLevel = parents.length > 0 ? parents : categories;

            return topLevel.map(cat => {
              const kids     = children.filter(ch => ch.parent_id === cat.id);
              // Count designs directly in this category OR any of its children
              const allIds   = [cat.id, ...kids.map(k => k.id)];
              const catCount = designs.filter(d => allIds.some(id => d.category?.includes(id))).length;
              const isActive = activeFilter === cat.id || kids.some(k => k.id === activeFilter);

              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveFilter(isActive && activeFilter !== cat.id ? cat.id : cat.id)}
                  className={`shrink-0 px-4 py-2 rounded-full text-xs font-bold transition-all flex items-center gap-1 ${
                    isActive ? 'bg-blush-300 text-white' : 'bg-white border border-sage-200 text-sage-500 hover:border-blush-100'
                  }`}
                >
                  {cat.label} ({catCount})
                  {kids.length > 0 && (
                    <SafeIcon name={isActive ? 'ChevronUp' : 'ChevronDown'} className="text-[10px] opacity-70" />
                  )}
                </button>
              );
            });
          })()}
        </div>

        {/* Subcategory row — only appears when a parent with children is active */}
        <AnimatePresence>
          {(() => {
            const parents  = categories.filter(c => !c.parent_id);
            const children = categories.filter(c =>  c.parent_id);
            const activeParent = parents.find(p =>
              activeFilter === p.id || children.some(ch => ch.parent_id === p.id && ch.id === activeFilter)
            );
            const kids = activeParent ? children.filter(ch => ch.parent_id === activeParent.id) : [];
            if (!kids.length) return null;

            return (
              <motion.div
                key={activeParent.id}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.18 }}
                className="overflow-hidden"
              >
                <div className="flex flex-wrap gap-2 px-1 pt-1">
                  {/* "All [Parent]" option */}
                  <button
                    onClick={() => setActiveFilter(activeParent.id)}
                    className={`shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold transition-all border ${
                      activeFilter === activeParent.id
                        ? 'bg-charcoal text-white border-charcoal'
                        : 'bg-white border-sage-200 text-sage-500 hover:border-sage-300'
                    }`}
                  >
                    All {activeParent.label}
                  </button>
                  {kids.map(child => {
                    const childCount = designs.filter(d => d.category?.includes(child.id)).length;
                    return (
                      <button
                        key={child.id}
                        onClick={() => setActiveFilter(child.id)}
                        className={`shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold transition-all border ${
                          activeFilter === child.id
                            ? 'bg-blush-200 border-blush-200 text-blush-700'
                            : 'bg-blush-50 border-blush-100 text-blush-400 hover:border-blush-300'
                        }`}
                      >
                        {child.label} ({childCount})
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            );
          })()}
        </AnimatePresence>
      </div>

      {/* Bulk action toolbar */}
      <AnimatePresence>
        {selectMode && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
            className="flex items-center justify-between gap-3 bg-charcoal text-white px-5 py-3 rounded-2xl shadow-soft"
          >
            <div className="flex items-center gap-3">
              <span className="text-sm font-bold">
                {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select designs to delete'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {selectedIds.size > 0 && (
                confirmBulkDelete ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-red-300 font-semibold">Delete {selectedIds.size} design{selectedIds.size > 1 ? 's' : ''}? This can't be undone.</span>
                    <button
                      onClick={handleBulkDelete}
                      disabled={bulkDeleting}
                      className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-xs font-bold transition-colors disabled:opacity-50"
                    >
                      {bulkDeleting ? 'Deleting...' : 'Confirm Delete'}
                    </button>
                    <button
                      onClick={() => setConfirmBulkDelete(false)}
                      className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-xs font-bold transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={handleBulkDelete}
                    className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors"
                  >
                    <SafeIcon name="Trash2" />
                    Delete {selectedIds.size}
                  </button>
                )
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
        {(() => {
          const visibleDesigns = designs.filter(d => {
            if (activeFilter === 'all') return true;
            if (activeFilter === 'next_day') return d.next_day_available;
            const childIds = categories.filter(c => c.parent_id === activeFilter).map(c => c.id);
            const allIds = [activeFilter, ...childIds];
            return d.category && allIds.some(id => d.category.includes(id));
          });

          return visibleDesigns.map(design => {
            const mainImg = design.main_image_index === 1 ? design.image_url_2 : design.image_url;
            const isSelected = selectedIds.has(design.id);
            return (
              <div
                key={design.id}
                className={`bg-white rounded-2xl border overflow-hidden group shadow-sm hover:shadow-soft transition-all relative ${
                  isSelected ? 'border-blush-300 ring-2 ring-blush-300' : 'border-sage-100'
                }`}
              >
                <div className="aspect-square relative overflow-hidden bg-sage-50">
                  <img src={mainImg || design.image_url} className="w-full h-full object-cover transition-transform group-hover:scale-105" alt={design.name} />
                  {!design.active && (
                    <div className="absolute inset-0 bg-white/60 flex items-center justify-center font-bold text-[10px] uppercase text-sage-400 tracking-widest">Inactive</div>
                  )}
                  {design.next_day_available && (
                    <div className="absolute top-2 left-2 bg-amber-400 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-sm">Next Day</div>
                  )}
                  {/* Checkbox overlay in select mode */}
                  {selectMode && (
                    <button
                      onClick={() => toggleSelectDesign(design.id)}
                      className="absolute inset-0 w-full h-full flex items-start justify-end p-2.5 z-10"
                    >
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shadow transition-all ${
                        isSelected
                          ? 'bg-blush-300 border-blush-300'
                          : 'bg-white/80 border-sage-300 hover:border-blush-300'
                      }`}>
                        {isSelected && <SafeIcon name="Check" className="text-white" style={{ fontSize: 12 }} />}
                      </div>
                    </button>
                  )}
                </div>
                <div className="p-4 space-y-3">
                  <p className="text-sm font-bold text-charcoal truncate">{design.name}</p>
                  <div className="flex gap-2">
                    {selectMode ? (
                      <button
                        onClick={() => toggleSelectDesign(design.id)}
                        className={`flex-1 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-colors ${
                          isSelected
                            ? 'bg-blush-100 text-blush-500 hover:bg-blush-200'
                            : 'bg-sage-50 text-sage-400 hover:bg-sage-100'
                        }`}
                      >
                        {isSelected ? 'Deselect' : 'Select'}
                      </button>
                    ) : (
                      <button onClick={() => openEdit(design)} className="flex-1 py-1.5 bg-sage-50 hover:bg-blush-50 hover:text-blush-500 rounded-lg text-[10px] font-bold uppercase transition-colors">Edit</button>
                    )}
                  </div>
                </div>
              </div>
            );
          });
        })()}
      </div>
    </div>
  );
}
